import { FILE_STORAGE_WARNING } from '../../../../core/utils/file-storage-error';
import { createUuid } from '../../../../core/utils/uuid';
import type { ProjectActor } from '../../../../core/interfaces/backend/proyectos/project-sections.interface';
import type { SectionCatalogs } from '../../../../core/interfaces/backend/proyectos/project-sections.interface';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { ProjectDialogService } from '../../../../core/services/project-dialog.service';
import { FormsModule } from '@angular/forms';
import type { SectionFile, SectionFileType } from '../../../../core/interfaces/http/section-file.interface';

@Component({ selector: 'app-project-actors', standalone: true, imports: [FormsModule], templateUrl: './project-actors.component.html', styleUrl: './project-actors.component.css' })
export class ProjectActorsComponent {
  get pendingUploadBlocked(){return !this.storageReady && this.files.some(f=>!!f.contenido);}
    private readonly dialogs = inject(ProjectDialogService);
    @Input({ required: true })
    actors!: ProjectActor[];
    @Input({ required: true })
    catalogs!: SectionCatalogs;
    @Input()
    instrument = '';
    @Input() saved = false;
    @Input() dirty = false;
    @Input()
    readOnly = false;
    @Input()
    saving = false;
    @Input()
    complete = false;
    @Input() files: SectionFile[] = [];
    @Input() fileTypes: SectionFileType[] = [];
    @Output() filesChange = new EventEmitter<SectionFile[]>();
    @Output() readingChange = new EventEmitter<boolean>();
    @Output() downloadFile = new EventEmitter<SectionFile>();
    @Input() storageUnavailable = false;
    @Input() storageReady = false;
    @Input() storageChecking = true;
    readonly storageWarning = FILE_STORAGE_WARNING;
    reading = false;
    fileError = '';
    @Output()
    changed = new EventEmitter<void>();
    @Output()
    save = new EventEmitter<void>();
    @Output()
    cancel = new EventEmitter<void>();
    get roles() { const ids = this.catalogs.roles_actores_instrumentos.filter(r => r.id_instrumento === this.instrument).map(r => r.id_rol_actor); return this.catalogs.roles_actores.filter(r => ids.includes(r.id_registro)); }
    types(role: string) { const ids = this.catalogs.roles_tipos_actores.filter(r => r.id_rol_actor === role).map(r => r.id_tipo_actor); const available = this.catalogs.tipos_actores_instrumentos.filter(r => r.id_instrumento === this.instrument).map(r => r.id_tipo_actor); return this.catalogs.tipos_actores.filter(r => ids.includes(r.id_registro) && available.includes(r.id_registro)); }
    roleName(role: string): string { return this.catalogs.roles_actores.find(item => item.id_registro === role)?.nombre ?? ''; }
    add(): void { this.actors.push({ id: createUuid(), role: '', type: '', entity: '', name: '' }); this.changed.emit(); }
    async remove(actor: ProjectActor): Promise<void> {
        if (this.readOnly || this.saving || !this.actors.includes(actor)) return;
        if (await this.dialogs.confirm('¿Está seguro que desea eliminar este actor? El cambio se aplicará al guardar la sección.')) {
        const index = this.actors.indexOf(actor);
        if (index < 0) return;
        this.actors.splice(index, 1);
        this.changed.emit();
    } }
    file(kind: string): SectionFile | undefined { return this.files.find(f => f.id_archivo_tipo === kind); }
    upload(event: Event, type: SectionFileType): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file || !this.storageReady || this.readOnly || this.saving || this.reading) return;
        if (file.size > 20 * 1024 * 1024 || !file.name.toLowerCase().endsWith('.pdf')) {
            this.fileError = 'Adjunte un PDF de máximo 20 MB.'; input.value = ''; return;
        }
        this.reading = true; this.readingChange.emit(true); this.fileError = '';
        const reader = new FileReader();
        const finish = () => { this.reading = false; this.readingChange.emit(false); input.value = ''; };
        reader.onerror = reader.onabort = () => { this.fileError = 'No se pudo leer el PDF.'; finish(); };
        reader.onload = () => {
            const contenido = String(reader.result).split(',')[1] ?? '';
            try {
                if (!atob(contenido.slice(0, 8)).startsWith('%PDF-')) throw new Error();
                const old = this.file(type.id_registro);
                this.files = [{id_registro:old?.id_registro,id_archivo_tipo:type.id_registro,nombre:file.name,tamano:file.size,contenido}];
                this.filesChange.emit(this.files); this.changed.emit();
            } catch { this.fileError = 'El archivo seleccionado no es un PDF válido.'; }
            finish();
        };
        reader.readAsDataURL(file);
    }
    async removeFile(kind: string): Promise<void> {
        if (!this.storageReady || this.readOnly || this.saving || this.reading) return;
        if (await this.dialogs.confirm('¿Eliminar el documento de caracterización? El cambio se aplicará al guardar.')) {
            this.files = this.files.filter(f => f.id_archivo_tipo !== kind);
            this.filesChange.emit(this.files); this.changed.emit();
        }
    }
}
